import { useEffect, useState, useCallback, useMemo } from "react";
import { creationLine } from "../../utils/creationInfo";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  FaPencilAlt,
  FaServer,
  FaCoffee,
  FaCogs,
  FaTint,
  FaLock,
  FaArrowLeft,
  FaRoute,
  FaTools,
  FaBan,
} from "react-icons/fa";
import InfoIcon from "@mui/icons-material/Info";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import { FaMapMarkerAlt } from "react-icons/fa";
import InfoTab from "../../Components/Device/Tabs/InfoTab";
import PartsTab from "../../Components/Device/Tabs/PartsTab";
import StatsTab from "../../Components/Device/Tabs/StatsTab";
import ReportTab from "../../Components/Device/Tabs/ReportTab";
import SettingsTab from "../../Components/Device/Tabs/SettingsTab";
import { isOkcDevice } from "../../Components/Device/Tabs/okcSettingsTab/okcSettingsModel";
import MapTab from "../../Components/Device/Tabs/MapTab";
import RemoteControlTab from "../../Components/Device/Tabs/RemoteControlTab";
import MaintenanceTab from "../../Components/Device/Tabs/maintenance/MaintenanceTab";
import { useWriteDeviceSettingsV2Mutation } from "../../store/storageRoles/devicesSettingsV2Api";
import DeviceMovementTable from "../../Components/Device/DeviceMovementTable";
import PaymentRejectionsTable from "../../Components/Device/PaymentRejectionsTable";
import { useLazyGetDeviceByIdQuery, useDeleteDeviceMutation, useUpdateDeviceNameMutation } from "../../store/storageRoles/devicesSharedApi";
import { useGetTrustedDevicesQuery } from "../../store";
import useUpdateDeviceData from "../../Hooks/device/useUpdateDeviceData";
import useUpdateDeviceSerialNo from "../../Hooks/device/useUpdateDeviceSerialNo";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";
import PasswordConfirmModal from "../../Components/Utils/PasswordConfirmModal";
import { getDeviceCupNumber, getGrinderStatus, getWaterModeStatus, shouldShowDevicePowerState } from "../../utils/deviceUtils";
import {
  DetailShell,
  DetailHeader,
  DetailTabs,
  DetailSkeleton,
  DetailNotFound,
} from "../../Components/shared";
import {
  useGetDeveloperPasswordMutation,
  useRefreshDeviceCredsMutation,
} from "../../store/storageRoles/devicesApi";
import { useAlert } from "../../context/AlertContext";
import { deviceActivityStateLabel } from "../../utils/deviceActivityLabels";

const ALL_TABS = [
  { id: "info", icon: InfoIcon, label: "Bilgi", bg: "bg-emerald-500", permission: "devices.info" },
  { id: "stats", icon: AssessmentIcon, label: "Tüketim", bg: "bg-blue-500", permission: "devices.stats" },
  // storageOnly: bayide Ayarlar, Bakım ve Hareket sekmeleri YOK (#302) —
  // eski şablonda izin kalsa da açılmaz (izin anahtarları usePermission'da da
  // bayi için düşer).
  { id: "settings", icon: SettingsIcon, label: "Ayarlar", bg: "bg-slate-500", permission: "devices.settings", storageOnly: true },
  { id: "remoteControl", icon: FaLock, label: "Uzaktan Kontrol", bg: "bg-rose-500", permission: "devices.settings", espressoOnly: true, storageOnly: true },
  // Hareket geçmişi devices.read'den AYRI izne bağlıdır: firma/plasiyer adları,
  // kota değişimleri ve operatör isimleri taşır — cihaz listesini görebilen
  // herkese açık olmamalı.
  // Bakım: canlı operasyon durumu (Çayset + Fincanda) ve Fincanda'da askı
  // sensörü canlı okuma + uzaktan hassasiyet (Workspace-#42).
  // Bakım kendi izniyle (#235) — önce devices.settings paylaşılıyordu
  { id: "maintenance", icon: FaTools, label: "Bakım", bg: "bg-teal-500", permission: "devices.maintenance", maintenanceOnly: true, storageOnly: true },
  { id: "movement", icon: FaRoute, label: "Hareket", bg: "bg-indigo-500", permission: "devices.movement", storageOnly: true },
  // Reddedilen QR/NFC ödemeleri (#410) — kullanıcı adı/firması taşır, ayrı izin
  { id: "paymentRejections", icon: FaBan, label: "Ödeme Retleri", bg: "bg-amber-500", permission: "devices.paymentRejections", storageOnly: true },
  { id: "parts", icon: FaCogs, label: "Parçalar", bg: "bg-orange-500", permission: "devices.parts" },
  { id: "location", icon: FaMapMarkerAlt, label: "Konum", bg: "bg-red-500", permission: "devices.location" },
  { id: "report", icon: DescriptionIcon, label: "Rapor", bg: "bg-violet-500", permission: "devices.reports" },
];

const getDeviceImage = (deviceName) => {
  if (!deviceName) return null;
  if (deviceName.includes("Çayset-Fincanda")) {
    return require("../../Assets/icons/caykahvebirarada.png");
  } else if (
    deviceName.includes("Çayset") ||
    deviceName.includes("Çay-Filtre Kahve")
  ) {
    return require("../../Assets/icons/cayfiltremakinesi.png");
  } else if (deviceName.includes("Fincanda")) {
    return require("../../Assets/icons/dortludegirmenli.png");
  } else if (deviceName.includes("Espresso")) {
    return require("../../Assets/icons/espresso_machine.png");
  }
  return null;
};

// Ortak çekirdek. Rol kabukları (storageRoles / dealerRoles) fetch hook'unu ve
// isDealer bağlamını geçirir; böylece bayi cihaz detayı dealer endpoint'inden
// beslenir (sayfa yenilemede de çalışır), depo storage endpoint'inden.
export default function DeviceDetailRoutePage({
  useDeviceDetailQuery = useLazyGetDeviceByIdQuery,
  isDealer = false,
}) {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const { hasPermission, isAdmin } = usePermission();

  const [device, setDevice] = useState(location.state?.device || null);
  const [devices, setDevices] = useState(device ? [device] : []);

  // Espresso modeli (uzaktan kontrol tab'i yalnizca bu modelde gosterilir).
  const isEspresso = useMemo(
    () =>
      (device?.deviceInfo?.deviceName || device?.deviceName || "").includes(
        "Espresso"
      ),
    [device]
  );

  // Bakım sekmesi: canlı durum yayını yapan makineler (Çayset, Çay-Filtre,
  // Fincanda ve birleşikleri). Espresso'da yok. Eski kart seti Türk kahvesi
  // (OKC) "Fincanda" adını taşır; #365'ten beri 4G kartı tabletin canlı
  // yayınlarını üretir (okc-live-status.md) — hassasiyet yazımı OKC'de yok.
  const hasMaintenance = useMemo(() => {
    if (isOkcDevice(device)) return true;
    const name = device?.deviceInfo?.deviceName || device?.deviceName || "";
    return (
      name.includes("Fincanda") ||
      name.includes("Çayset") ||
      name.includes("Çay-Filtre")
    );
  }, [device]);

  const TABS = useMemo(() => {
    return ALL_TABS.filter((tab) => {
      if (tab.espressoOnly && !isEspresso) return false;
      if (tab.maintenanceOnly && !hasMaintenance) return false;
      if (tab.storageOnly && isDealer) return false;
      if (tab.permission) return hasPermission(tab.permission);
      return true;
    });
  }, [hasPermission, isEspresso, isDealer, hasMaintenance]);

  const [selectedTab, setSelectedTab] = useState(() => TABS[0]?.id || "info");
  const [alerts, setAlerts] = useState([]);

  // Edit states
  const [editingCustomName, setEditingCustomName] = useState(false);
  const [customNameText, setCustomNameText] = useState("");
  const [editingDeviceName, setEditingDeviceName] = useState(false);
  const [deviceNameText, setDeviceNameText] = useState("");
  const [editingSerialNo, setEditingSerialNo] = useState(false);
  const [serialNoText, setSerialNoText] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [refreshCredsModalOpen, setRefreshCredsModalOpen] = useState(false);
  const [confirmChangeModalOpen, setConfirmChangeModalOpen] = useState(false);
  const [pendingChangeType, setPendingChangeType] = useState(null);
  // Developer parolasi modal — handleShowDeveloperPassword direkt API
  // cagiriyor, sonuc burada (devPwdValue) gosterilir. Confirm modali yok.
  const [devPwdValue, setDevPwdValue] = useState(null);
  const [devPwdProvisioned, setDevPwdProvisioned] = useState(true);
  // Kart Firmware drawer — bu sayfa gerçek, tekil cihaz (serialNo + deviceInfo)
  // tuttuğu için push burada çalışır (liste seviyesindeki header'da çalışmaz).
  const { showError } = useAlert();
  const [getDeveloperPassword, { isLoading: devPwdLoading }] =
    useGetDeveloperPasswordMutation();

  // API
  const [getDeviceById, { data: deviceData, isLoading, isFetching }] =
    useDeviceDetailQuery();
  // Geri navigasyon / liste yolu role göre
  const listPath = isDealer ? "/Anasayfa/Cihazlarım" : "/Anasayfa/Cihazlar";
  const [deleteDevice, { isLoading: deleteDeviceLoading }] = useDeleteDeviceMutation();
  const [refreshDeviceCreds, { isLoading: refreshCredsLoading }] =
    useRefreshDeviceCredsMutation();
  const { data: devicesData } = useGetTrustedDevicesQuery(undefined, { skip: !auth?.accessToken });
  const twoFactorEnabled = devicesData?.data?.twoFactorEnabled === true;

  const {
    updateDevice,
    updateDeviceIsLoading,
    updateDeviceIsSuccess,
    updateDeviceIsError,
    updateDeviceError,
  } = useUpdateDeviceData();

  const {
    updateDeviceSerialNo,
    updateDeviceSerialNoIsLoading,
    updateDeviceSerialNoIsSuccess,
    updateDeviceSerialNoIsError,
    updateDeviceSerialNoError,
  } = useUpdateDeviceSerialNo();

  const [
    updateDeviceName,
    {
      isLoading: updateDeviceNameIsLoading,
      isSuccess: updateDeviceNameIsSuccess,
      isError: updateDeviceNameIsError,
      error: updateDeviceNameError,
    },
  ] = useUpdateDeviceNameMutation();

  const fetchDevice = useCallback(() => {
    if (auth?.accessToken && deviceId) {
      getDeviceById({ token: auth.accessToken, deviceId, limit: 1 });
    }
  }, [auth?.accessToken, deviceId, getDeviceById]);

  useEffect(() => {
    fetchDevice();
  }, [fetchDevice]);

  useEffect(() => {
    if (deviceData?.devices?.length > 0) {
      const fetched = deviceData.devices[0];
      setDevice(fetched);
      setDevices([fetched]);
    }
  }, [deviceData]);

  useEffect(() => {
    if (device) {
      setCustomNameText(device.customName || "");
      setDeviceNameText(device.deviceInfo?.deviceName || device.deviceName || "");
      setSerialNoText(device.serialNo || "");
    }
  }, [device]);

  const handleUpdateCustomName = () => {
    if (!device) return;
    updateDevice({
      note: device.note,
      customName: customNameText,
      token: auth.accessToken,
      id: device._id,
    });
    addAlert("Özel isim güncelleniyor...", "info", 2000);
  };

  const handleUpdateDeviceName = () => {
    if (!device || !deviceNameText.trim()) return;
    setPendingChangeType("deviceName");
    setConfirmChangeModalOpen(true);
  };

  useEffect(() => {
    if (updateDeviceIsSuccess) {
      addAlert("Özel isim güncellendi", "success");
      setEditingCustomName(false);
      setDevice((prev) =>
        prev ? { ...prev, customName: customNameText } : null
      );
      setDevices((prev) =>
        prev.map((d) =>
          d._id === device?._id ? { ...d, customName: customNameText } : d
        )
      );
    }
    if (updateDeviceIsError) {
      addAlert(
        updateDeviceError?.data?.message || "Özel isim güncellenirken hata oluştu",
        "error"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDeviceIsSuccess, updateDeviceIsError]);

  useEffect(() => {
    if (updateDeviceNameIsSuccess) {
      addAlert("Makine model adı güncellendi", "success");
      setEditingDeviceName(false);
      setDevice((prev) =>
        prev
          ? {
              ...prev,
              deviceName: deviceNameText,
              deviceInfo: prev.deviceInfo
                ? { ...prev.deviceInfo, deviceName: deviceNameText }
                : prev.deviceInfo,
            }
          : null
      );
      setDevices((prev) =>
        prev.map((d) =>
          d._id === device?._id
            ? {
                ...d,
                deviceName: deviceNameText,
                deviceInfo: d.deviceInfo
                  ? { ...d.deviceInfo, deviceName: deviceNameText }
                  : d.deviceInfo,
              }
            : d
        )
      );
    }
    if (updateDeviceNameIsError) {
      addAlert(
        updateDeviceNameError?.data?.message ||
          "Makine model adı güncellenirken hata oluştu",
        "error"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDeviceNameIsSuccess, updateDeviceNameIsError]);

  const handleUpdateSerialNo = () => {
    if (!device) return;
    setPendingChangeType("serialNo");
    setConfirmChangeModalOpen(true);
  };

  useEffect(() => {
    if (updateDeviceSerialNoIsSuccess) {
      addAlert("Seri numarası başarıyla güncellendi", "success");
      setEditingSerialNo(false);
      setDevice((prev) => (prev ? { ...prev, serialNo: serialNoText } : prev));
      setDevices((prev) =>
        prev.map((d) =>
          d._id === device?._id ? { ...d, serialNo: serialNoText } : d
        )
      );
    }
    if (updateDeviceSerialNoIsError) {
      addAlert(
        updateDeviceSerialNoError?.data?.message || "Seri numarası güncellenirken hata oluştu",
        "error"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDeviceSerialNoIsSuccess, updateDeviceSerialNoIsError]);

  const addAlert = (message, severity, autoRemoveMs = 3000) => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, message, severity }]);
    setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    }, autoRemoveMs);
  };

  // Developer parolasini direkt al — server controlPassword middleware'i
  // kaldirildigi icin password + 2FA istemiyor; admin tek tikla modalda
  // parolayi gorur (audit log server tarafinda korunuyor).
  const handleShowDeveloperPassword = async () => {
    if (!device?._id || !auth?.accessToken) return;
    try {
      const res = await getDeveloperPassword({
        token: auth.accessToken,
        deviceId: device._id,
      }).unwrap();

      const data = res?.data || {};
      setDevPwdProvisioned(Boolean(data.provisioned));
      setDevPwdValue(data.password || null);
      if (!data.provisioned) {
        showError(
          "Bu cihaz henüz developer parolası için provisioned değil. Yeni APK ile pending'e düşünce admin onayı gerek."
        );
      }
    } catch (err) {
      const msg = err?.data?.message || "Developer parolası alınamadı.";
      showError(msg);
    }
  };

  // 30sn auto-hide — devPwdValue set olunca 30sn sonra otomatik gizlenir
  // (inline box'taki göz reveal'i de buna bağlı sıfırlanır).
  useEffect(() => {
    if (!devPwdValue) return;
    const t = setTimeout(() => {
      setDevPwdValue(null);
    }, 30_000);
    return () => clearTimeout(t);
  }, [devPwdValue]);

  const handleDeleteDevice = async ({ password, passwordConfirm, code }) => {
    if (!device?._id || !auth?.accessToken) return;
    try {
      await deleteDevice({
        id: device._id,
        password,
        passwordConfirm,
        ...(twoFactorEnabled && code ? { code } : {}),
        token: auth.accessToken,
      }).unwrap();
      addAlert("Cihaz silindi.", "success");
      setDeleteModalOpen(false);
      navigate(listPath);
    } catch (err) {
      addAlert("İşlem başarısız.", "error");
    }
  };

  // Creds Yenile — server per-device secret'lari rotate edip cihaza MQTT ile
  // gonderir. HTTP cevabi "gonderildi"yi bildirir; cihaz uygulayinca socket
  // "device:credsRefreshAck" ile onay gelir (server tarafinda loglanir).
  const handleRefreshCreds = async ({ password, passwordConfirm, code }) => {
    if (!device?._id || !auth?.accessToken) return;
    try {
      // Server cihazın ACK'ini bekler → res.data.acked gerçek uygulama onayı.
      // acked=false ise "gönderildi ama cihaz onaylamadı" — başarı DEME.
      const res = await refreshDeviceCreds({
        id: device._id,
        password,
        passwordConfirm,
        ...(twoFactorEnabled && code ? { code } : {}),
        token: auth.accessToken,
      }).unwrap();
      const data = res?.data || {};
      if (data.acked) {
        addAlert(
          data.message || "Cihaz yeni kimlik bilgilerini aldı ve uyguladı.",
          "success"
        );
      } else {
        addAlert(
          data.message ||
            "Cihaza gönderildi ama onay gelmedi. Cihaz çevrimdışı olabilir.",
          "error"
        );
      }
      setRefreshCredsModalOpen(false);
    } catch (err) {
      const msg = err?.data?.message || "İşlem başarısız.";
      addAlert(msg, "error");
    }
  };

  const handleConfirmChange = async ({ password, passwordConfirm, code }) => {
    if (!device?._id || !auth?.accessToken || !pendingChangeType) return;
    try {
      if (pendingChangeType === "serialNo") {
        await updateDeviceSerialNo({
          id: device._id,
          serialNo: serialNoText,
          password,
          passwordConfirm,
          ...(twoFactorEnabled && code ? { code } : {}),
          token: auth.accessToken,
        }).unwrap();
      } else if (pendingChangeType === "deviceName") {
        await updateDeviceName({
          id: device._id,
          deviceName: deviceNameText.trim(),
          password,
          passwordConfirm,
          ...(twoFactorEnabled && code ? { code } : {}),
          token: auth.accessToken,
        }).unwrap();
      }
      addAlert(pendingChangeType === "serialNo" ? "Seri numarası güncelleniyor..." : "Makine model adı güncelleniyor...", "info", 2000);
      setConfirmChangeModalOpen(false);
      setPendingChangeType(null);
    } catch (err) {
      addAlert(err?.data?.message || "İşlem başarısız.", "error");
    }
  };

  const handleGoBack = () => {
    if (location.state?.fromPage === "Cihazlar" && location.state?.deviceListFilters) {
      navigate(listPath, {
        state: { deviceListFilters: location.state.deviceListFilters },
      });
    } else if (location.state?.fromPage === "Anasayfa/Firma" && location.state?.fromFirmId) {
      // Firma detayından (Cihazlar sekmesi) gelindi — navigate(-1) firma
      // sayfasını ESKİ history state'i ile açar ve sekme Info'ya düşer.
      // Bu yüzden firma detayına toPageTab ile hedefli dönülür (route iki
      // rolde de ortak: /Anasayfa/Firma/:firmId).
      navigate(`/Anasayfa/Firma/${location.state.fromFirmId}`, {
        state: { toPageTab: location.state?.fromPageTab || "devices" },
      });
    } else if (location.state?.fromPage) {
      navigate(-1);
    } else {
      navigate(listPath);
    }
  };

  // Loading
  if (!device && isLoading) {
    return <DetailSkeleton tabCount={5} sectionRows={[4, 3]} />;
  }

  // Not found
  if (!device && !isLoading) {
    return (
      <DetailNotFound
        icon={FaServer}
        title="Cihaz Bulunamadı"
        message="İstenen cihaz bilgilerine ulaşılamadı."
        backLabel="Cihazlara Dön"
        onGoBack={() => navigate(listPath)}
      />
    );
  }

  // No tab permission
  if (TABS.length === 0) {
    return (
      <DetailNotFound
        icon={FaServer}
        title="Erişim Yetkisi Yok"
        message="Bu cihazın detaylarını görüntüleme yetkiniz bulunmuyor."
        backLabel="Cihazlara Dön"
        onGoBack={() => navigate(listPath)}
      />
    );
  }

  const deviceName = device.deviceInfo?.deviceName || device.deviceName || "";
  const isConnected = device.deviceInfo?.connection?.status;
  const deviceImage = getDeviceImage(deviceName);

  return (
    <DetailShell
      alerts={alerts}
      onDismissAlert={(id) => setAlerts((prev) => prev.filter((a) => a.id !== id))}
    >
      {/* Header */}
      <DetailHeader
        onGoBack={handleGoBack}
        backTitle="Cihazlara Dön"
        backContent={
          <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-SemiBold whitespace-nowrap">
            <FaArrowLeft className="w-3.5 h-3.5" />
            Geri
          </span>
        }
      >
        {/* Cihaz Bilgileri — TEK SATIR.
            Sol: görsel + seri no + model/özel isim (yer daralınca truncate).
            Sağ: durum rozetleri (asla sarmaz; dar ekranda şerit yatay kayar). */}
        <div className="flex-1 min-w-0 flex items-center gap-2 sm:gap-3 overflow-x-auto no-scrollbar">
          {deviceImage && (
            <img
              src={deviceImage}
              alt={deviceName}
              className="h-7 w-7 sm:h-9 sm:w-9 shrink-0 object-contain rounded-lg bg-white border border-gray-200 p-0.5"
            />
          )}

          {editingSerialNo ? (
            <div className="flex items-center gap-1.5 shrink-0">
              <input
                type="text"
                value={serialNoText}
                onChange={(e) => setSerialNoText(e.target.value)}
                className="text-sm sm:text-base font-Bold text-gray-800 bg-white border border-blue-300 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400 w-40 sm:w-52"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdateSerialNo();
                  if (e.key === "Escape") { setEditingSerialNo(false); setSerialNoText(device.serialNo || ""); }
                }}
              />
              <button onClick={handleUpdateSerialNo} disabled={updateDeviceSerialNoIsLoading || serialNoText === device.serialNo} className="p-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 disabled:opacity-50" title="Kaydet">
                {updateDeviceSerialNoIsLoading ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
              </button>
              <button onClick={() => { setEditingSerialNo(false); setSerialNoText(device.serialNo || ""); }} className="p-1 bg-gray-400 text-white rounded-md hover:bg-gray-500" title="İptal">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          ) : (
            <>
              {/* Kimlik: seri no • model • özel isim */}
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 group">
                {/* Seri no; altında oluşturulma bilgisi (#343) */}
                <div className="flex flex-col min-w-0">
                  <h1 className="text-sm sm:text-base font-Bold text-gray-800 whitespace-nowrap">{device.serialNo}</h1>
                  <span className="text-[11px] leading-tight text-gray-400 whitespace-nowrap">
                    {creationLine(device)}
                  </span>
                </div>
                {hasPermission("devices.editSerial") && (
                  <button onClick={() => setEditingSerialNo(true)} className="p-0.5 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-all shrink-0" title="Seri No Düzenle">
                    <FaPencilAlt className="w-2.5 h-2.5" />
                  </button>
                )}
                {editingDeviceName ? (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <input
                      type="text"
                      value={deviceNameText}
                      onChange={(e) => setDeviceNameText(e.target.value)}
                      placeholder="Makine model adı..."
                      className="text-sm sm:text-base text-gray-800 bg-white border border-blue-300 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[180px] sm:min-w-[220px]"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleUpdateDeviceName();
                        if (e.key === "Escape") { setEditingDeviceName(false); setDeviceNameText(device.deviceInfo?.deviceName || device.deviceName || ""); }
                      }}
                    />
                    <button onClick={handleUpdateDeviceName} disabled={updateDeviceNameIsLoading || !deviceNameText.trim() || deviceNameText.trim() === (device.deviceInfo?.deviceName || device.deviceName || "")} className="p-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 disabled:opacity-50" title="Kaydet">
                      {updateDeviceNameIsLoading ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                    </button>
                    <button onClick={() => { setEditingDeviceName(false); setDeviceNameText(device.deviceInfo?.deviceName || device.deviceName || ""); }} className="p-1 bg-gray-400 text-white rounded-md hover:bg-gray-500" title="İptal">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ) : editingCustomName ? (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-gray-300" aria-hidden="true">•</span>
                    <span className="text-sm sm:text-base text-gray-600 whitespace-nowrap">{deviceName}</span>
                    <span className="text-gray-300" aria-hidden="true">•</span>
                    <input
                      type="text"
                      value={customNameText}
                      onChange={(e) => setCustomNameText(e.target.value)}
                      placeholder="Özel isim..."
                      className="text-sm sm:text-base text-gray-800 bg-white border border-blue-300 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400 w-32 sm:w-44"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleUpdateCustomName();
                        if (e.key === "Escape") { setEditingCustomName(false); setCustomNameText(device.customName || ""); }
                      }}
                    />
                    <button onClick={handleUpdateCustomName} disabled={updateDeviceIsLoading || customNameText === (device.customName || "")} className="p-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 disabled:opacity-50" title="Kaydet">
                      {updateDeviceIsLoading ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                    </button>
                    <button onClick={() => { setEditingCustomName(false); setCustomNameText(device.customName || ""); }} className="p-1 bg-gray-400 text-white rounded-md hover:bg-gray-500" title="İptal">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 min-w-0 group/custom">
                    <span className="text-gray-300 shrink-0" aria-hidden="true">•</span>
                    <span className="text-sm sm:text-base text-gray-600 truncate">
                      {deviceName}
                      {device.customName ? (
                        <span className="text-gray-500"> • {device.customName}</span>
                      ) : (
                        // Özel isim yoksa "Özel isim ekle" ipucu yalnızca düzenleme
                        // izni olanlara gösterilir (bayide devices.editName yok →
                        // boş kalır).
                        hasPermission("devices.editName") ? (
                          <span className="text-gray-400 italic"> • Özel isim ekle</span>
                        ) : null
                      )}
                    </span>
                    {hasPermission("devices.editModelName") && (
                      <button onClick={() => setEditingDeviceName(true)} className="p-0.5 text-gray-400 hover:text-blue-600 opacity-0 group-hover/custom:opacity-100 transition-all shrink-0" title="Makine model adını düzenle">
                        <FaPencilAlt className="w-2.5 h-2.5" />
                      </button>
                    )}
                    {hasPermission("devices.editName") && (
                      <button onClick={() => setEditingCustomName(true)} className="p-0.5 text-gray-400 hover:text-blue-600 opacity-0 group-hover/custom:opacity-100 transition-all shrink-0" title="Özel İsmi Düzenle">
                        <FaPencilAlt className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Kimlik ile donanım rozetlerini ayıran ince çizgi */}
              <span className="hidden md:block h-5 w-px bg-gray-300/80 shrink-0" aria-hidden="true" />

              {/* Donanım rozetleri — göz / değirmen / su modu */}
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-SemiBold text-slate-700 bg-slate-100 rounded-full px-2 sm:px-2.5 py-0.5 whitespace-nowrap">
                  <FaCoffee className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                  {getDeviceCupNumber(device)} göz
                </span>

                {/* Değirmen/su modu header'da: server componentSummary'sine değil,
                    gerçek component verisine bakılır (aktif değilse "yok"; her
                    yerde tutarlı, restart bağımsız). */}
                {getGrinderStatus(device).label !== "-" && (
                  <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-SemiBold text-amber-800 bg-amber-50 rounded-full px-2 sm:px-2.5 py-0.5 whitespace-nowrap">
                    <FaCogs className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                    Değirmen
                  </span>
                )}

                {(() => {
                  const wm = getWaterModeStatus(device);
                  if (wm.label === "-") return null;
                  return (
                    <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-SemiBold text-blue-800 bg-blue-50 rounded-full px-2 sm:px-2.5 py-0.5 whitespace-nowrap">
                      <FaTint className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      {wm.label}
                    </span>
                  );
                })()}
              </div>

              {/* Canlı durum (Bağlı / Açık) satırın EN SAĞINDA — ml-auto ile
                  boşluğu yiyip sağ kenara yapışır; dar ekranda şeridin sonunda
                  kalır (satır zaten yatay kayıyor). */}
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 ml-auto pl-2">
                <span className={`inline-flex items-center gap-1.5 text-xs sm:text-sm font-SemiBold rounded-full px-2 sm:px-2.5 py-0.5 whitespace-nowrap ${isConnected ? "text-emerald-700 bg-emerald-50" : "text-red-600 bg-red-50"}`}>
                  <span className="relative flex h-2 w-2 shrink-0">
                    {isConnected && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />}
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${isConnected ? "bg-emerald-500" : "bg-red-500"}`} />
                  </span>
                  {isConnected ? "Bağlı" : "Bağlı Değil"}
                </span>

                {/* Açık/Kapalı — bayi panelinde bayinin kendi firmasındaki
                    cihazlarda basılmaz (sahada değil, depoda/serviste duruyor) */}
                {shouldShowDevicePowerState(auth, device) && (
                  <span className={`inline-flex items-center gap-1.5 text-xs sm:text-sm font-SemiBold rounded-full px-2 sm:px-2.5 py-0.5 whitespace-nowrap ${device.isActive ? "text-green-700 bg-green-50" : "text-red-600 bg-red-50"}`}>
                    <svg className={`w-3.5 h-3.5 shrink-0 ${device.isActive ? "text-green-600" : "text-red-500"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
                    </svg>
                    {deviceActivityStateLabel(device.isActive)}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Sağ: loading. Admin işlemleri (Developer Parolası / Cihazı Sil)
            Bilgi tabındaki "Cihaz Bilgileri" box'ına taşındı. */}
        {isFetching && (
          <div className="shrink-0 flex items-center">
            <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </DetailHeader>

      <PasswordConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteDevice}
        title="Cihaz Silme İşlemi"
        subtitle={`Bu işlem geri alınamaz. Lütfen şifrenizi girerek onaylayın.${twoFactorEnabled ? " İki adımlı doğrulama kodu da gereklidir." : ""}`}
        confirmButtonText="Cihazı Sil"
        isLoading={deleteDeviceLoading}
        twoFactorEnabled={twoFactorEnabled}
      />

      <PasswordConfirmModal
        open={refreshCredsModalOpen}
        onClose={() => setRefreshCredsModalOpen(false)}
        onConfirm={handleRefreshCreds}
        title="Kimlik Bilgilerini Yenile"
        subtitle={`Cihazın kimlik bilgileri yeniden üretilip cihaza gönderilecek. Onaylamak için şifrenizi girin.${twoFactorEnabled ? " İki adımlı doğrulama kodu da gereklidir." : ""}`}
        confirmButtonText="Yenile ve Gönder"
        isLoading={refreshCredsLoading}
        twoFactorEnabled={twoFactorEnabled}
      />

      <PasswordConfirmModal
        open={confirmChangeModalOpen}
        onClose={() => { setConfirmChangeModalOpen(false); setPendingChangeType(null); }}
        onConfirm={handleConfirmChange}
        title={pendingChangeType === "serialNo" ? "Seri No Değişikliği" : "Model Adı Değişikliği"}
        subtitle={`Değişikliği onaylamak için şifrenizi girin.${twoFactorEnabled ? " İki adımlı doğrulama kodu da gereklidir." : ""}`}
        confirmButtonText="Kaydet"
        isLoading={updateDeviceSerialNoIsLoading || updateDeviceNameIsLoading}
        twoFactorEnabled={twoFactorEnabled}
      />

      {/* Developer Parolası artık Bilgi tabındaki "Cihaz Bilgileri" box'ında
          göz ikonuyla inline gösteriliyor; ayrı gösterim modalı kaldırıldı.
          (30sn auto-hide ve fetch mantığı bu sayfada kalmaya devam ediyor.) */}

      {/* Tab Navigation */}
      <DetailTabs tabs={TABS} activeTab={selectedTab} onChange={setSelectedTab} />

      {/* Tab Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {selectedTab === "info" && hasPermission("devices.info") && (
          <div className="h-full bg-gray-50/40">
            <InfoTab
              selectedDevice={device}
              setDevice={setDevice}
              setDevices={setDevices}
              setAlerts={setAlerts}
              onShowDeveloperPassword={
                hasPermission("devices.developerPassword")
                  ? handleShowDeveloperPassword
                  : undefined
              }
              onHideDeveloperPassword={() => setDevPwdValue(null)}
              developerPassword={devPwdValue}
              devPwdLoading={devPwdLoading}
              onRequestDelete={isAdmin ? () => setDeleteModalOpen(true) : undefined}
              onRequestRefreshCreds={
                isAdmin ? () => setRefreshCredsModalOpen(true) : undefined
              }
              refreshCredsLoading={refreshCredsLoading}
            />
          </div>
        )}
        {selectedTab === "movement" && hasPermission("devices.movement") && !isDealer && (
          <div className="h-full bg-gray-50/40">
            <DeviceMovementTable
              deviceId={device._id}
              token={auth.accessToken}
            />
          </div>
        )}
        {selectedTab === "paymentRejections" && hasPermission("devices.paymentRejections") && !isDealer && (
          <div className="h-full bg-gray-50/40">
            <PaymentRejectionsTable deviceId={device._id} token={auth.accessToken} />
          </div>
        )}
        {selectedTab === "parts" && hasPermission("devices.parts") && (
          <div className="h-full">
            <PartsTab selectedDevice={device} />
          </div>
        )}
        {selectedTab === "stats" && hasPermission("devices.stats") && (
          <div className="h-full">
            <StatsTab selectedDevice={device} />
          </div>
        )}
        {selectedTab === "report" && hasPermission("devices.reports") && (
          <div className="h-full">
            <ReportTab selectedDevice={device} />
          </div>
        )}
        {/* Ayarlar sekmesi yatay padding'i BİLEREK yok: sekmenin kendi sabit
            başlığı kutunun kenarına kadar uzansın (alt kenarlık tam satır).
            Yatay boşluğu SettingsTabV2 kendi içinde veriyor. */}
        {selectedTab === "settings" && hasPermission("devices.settings") && !isDealer && (
          <div className="h-full overflow-y-auto bg-gray-50/40">
            <SettingsTab setAlerts={setAlerts} selectedDevice={device} />
          </div>
        )}
        {selectedTab === "remoteControl" &&
          hasPermission("devices.settings") &&
          !isDealer &&
          isEspresso && (
            <div className="h-full">
              <RemoteControlTab selectedDevice={device} />
            </div>
          )}
        {selectedTab === "maintenance" &&
          hasPermission("devices.maintenance") &&
          !isDealer &&
          hasMaintenance && (
            <div className="h-full">
              <MaintenanceTab
                selectedDevice={device}
                useWriteSettings={useWriteDeviceSettingsV2Mutation}
              />
            </div>
          )}
        {selectedTab === "location" && hasPermission("devices.location") && (
          <div className="h-full overflow-y-auto p-3 sm:p-4 bg-gray-50/40">
            <MapTab selectedDevice={device} />
          </div>
        )}
      </div>
    </DetailShell>
  );
}
